import { Chip, styled, useTheme } from "@mui/material";

import { JobStatus } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";

type JobStatusChipProps = {
  jobStatus: JobStatus;
};

type StatusConfig = {
  label: string;
  backgroundColor: string;
  textColor: string;
};

const StyledChip = styled(Chip, { shouldForwardProp: isPropValid })<{
  $backgroundColor: string;
  $textColor: string;
}>`
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  font-size: 12px;
  font-weight: 400;

  .MuiChip-label {
    padding: ${(props) => props.theme.spacing(0, Spacing.M)};
  }
`;

export const JobStatusChip = ({
  jobStatus,
}: JobStatusChipProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const getStatusConfig = (): StatusConfig => {
    switch (jobStatus) {
      case JobStatus.Done:
        return {
          label: t(Resources.Common.StatusDone),
          backgroundColor: theme.palette.success.dark,
          textColor: theme.palette.success.light,
        };
      case JobStatus.WaitingForApproval:
        return {
          label: t(Resources.Common.StatusWaitingForApproval),
          backgroundColor: theme.palette.warning.dark,
          textColor: theme.palette.warning.light,
        };
      case JobStatus.RejectedByActor:
        return {
          label: t(Resources.Common.StatusRejectedByActor),
          backgroundColor: theme.palette.error.dark,
          textColor: theme.palette.error.light,
        };
      default:
        return {
          label: t(Resources.Common.StatusInProgress),
          backgroundColor: theme.palette.info.dark,
          textColor: theme.palette.info.light,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <StyledChip
      label={config.label}
      $backgroundColor={config.backgroundColor}
      $textColor={config.textColor}
    />
  );
};
